import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { Button } from './ui/button';

const ExamplesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const examples = [
    {
      day: 1,
      message: 'Dnes si dejte 10 minut jen pro sebe dva. Žádné telefony. Jen si sedněte a povídejte si o tom, co vás poslední dobou těší.',
      type: 'gentle',
    },
    {
      day: 3,
      message: 'Napiš partnerovi 3 věci, za které jsi mu/jí dnes vděčný/á. Pošli to jako SMS během dne.',
      type: 'gentle',
    },
    {
      day: 5,
      message: 'Večer si uvařte společně večeři. Žádné recepty z internetu – improvizujte s tím, co máte v lednici.',
      type: 'adventure',
    },
    {
      day: 7,
      message: 'Každý z vás vytvoří pro druhého malý "voucher" na něco hezkého. Třeba masáž, snídaně do postele, nebo společný výlet.',
      type: 'gentle',
    },
    {
      day: 10,
      message: 'Dnes budete mít "date night" – ale naplánuje to ten druhý. První o tom netuší.',
      type: 'adventure',
    },
    {
      day: 14,
      message: 'Finále! Napište si navzájem dopisy o tom, co pro vás ty dva týdny znamenaly. Přečtěte si je společně.',
      type: 'gentle',
    },
  ];

  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % examples.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoPlay, examples.length]);

  const goToPrevious = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + examples.length) % examples.length);
  };

  const goToNext = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % examples.length);
  };

  return (
    <section id="examples" className="py-20 bg-white">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-semibold text-[var(--vp-ink)] mb-4">
            Ukázky výzev
          </h2>
          <p className="text-[var(--vp-ink)]/70 max-w-2xl mx-auto mb-4">
            Nahlédněte, jaké typy výzev vás čekají během 14 dní.
          </p>
          <div className="inline-flex items-center gap-2 text-sm text-[var(--vp-pink)] bg-[var(--vp-pink)]/10 px-4 py-2 rounded-full">
            <Info className="w-4 h-4" />
            <span>Ilustrační ukázky – skutečné výzvy v balíčku se liší</span>
          </div>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          {/* Main message display */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-[var(--vp-paper)] rounded-3xl p-8 border border-[var(--vp-line)] min-h-[200px]"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[var(--vp-ink)] flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--vp-ink)]">Výzva přijata</p>
                    <p className="text-sm text-[var(--vp-ink)]/60">
                      Den {examples[currentIndex].day}/14
                    </p>
                  </div>
                  <span
                    className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                      examples[currentIndex].type === 'gentle'
                        ? 'bg-[var(--vp-mint)]/20 text-[var(--vp-ink)]'
                        : 'bg-[var(--vp-pink)]/20 text-[var(--vp-pink)]'
                    }`}
                  >
                    {examples[currentIndex].type === 'gentle' ? 'Ukázka' : 'Ukázka'}
                  </span>
                </div>

                <p className="text-lg text-[var(--vp-ink)] leading-relaxed">
                  {examples[currentIndex].message}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="absolute top-1/2 -translate-y-1/2 -left-4 sm:-left-6">
              <Button
                onClick={goToPrevious}
                variant="outline"
                size="icon"
                className="w-10 h-10 rounded-full bg-white shadow-lg border-[var(--vp-line)]"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 -right-4 sm:-right-6">
              <Button
                onClick={goToNext}
                variant="outline"
                size="icon"
                className="w-10 h-10 rounded-full bg-white shadow-lg border-[var(--vp-line)]"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-6">
            {examples.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setAutoPlay(false);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex
                    ? 'bg-[var(--vp-pink)]'
                    : 'bg-[var(--vp-line)] hover:bg-[var(--vp-ink)]/30'
                }`}
              />
            ))}
          </div>

          {/* Additional info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12 grid sm:grid-cols-3 gap-4 text-center"
          >
            <div className="bg-[var(--vp-sand)] rounded-2xl p-4">
              <p className="text-2xl font-semibold text-[var(--vp-ink)]" style={{ fontFamily: 'var(--font-display)' }}>
                14
              </p>
              <p className="text-sm text-[var(--vp-ink)]/60">dní výzev</p>
            </div>
            <div className="bg-[var(--vp-sand)] rounded-2xl p-4">
              <p className="text-2xl font-semibold text-[var(--vp-ink)]" style={{ fontFamily: 'var(--font-display)' }}>
                2
              </p>
              <p className="text-sm text-[var(--vp-ink)]/60">SMS denně</p>
            </div>
            <div className="bg-[var(--vp-sand)] rounded-2xl p-4">
              <p className="text-2xl font-semibold text-[var(--vp-pink)]" style={{ fontFamily: 'var(--font-display)' }}>
                ∞
              </p>
              <p className="text-sm text-[var(--vp-ink)]/60">společných momentů</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ExamplesSection;
